/**
 * Country-specific Template Configuration (SEO legacy, admin, timezone helpers, tracking helpers).
 *
 * No es el mapa de la home: esa fuente es el Worker + `workerCountryTemplateMap.ts`.
 * Varios `template` aquí (p. ej. BioLinkTemplate por país) pueden diferir de producción.
 *
 * Define which template to show for each LATAM country
 *
 * ── BioTemplatePremium ────────────────────────────────────────────────────────
 * Para activar BioTemplatePremium en un país:
 *   1. Cambiar template: "BioTemplatePremium" en la entrada del país (abajo)
 *   2. Actualizar el Cloudflare Worker para que ese país sirva /invitacion-premium
 *
 * Moneda de checkout (automático — NO depende del campo `currency` de este config):
 *   AR  → "ARS" → Mercado Pago (suscripción mensual)
 *   any → "USD" → Stripe (pago único)
 *
 * Precio e idioma → se leen desde env vars y del campo `language` de este config.
 * Videos Mux      → env vars VITE_PREMIUM_MUX_*
 */

import { getServerTemplateConfig } from "@/lib/serverTemplateDetection";

export type TemplateType = "BaseTemplate" | "BioLinkTemplate" | "ArgentinaBioTemplate" | "MaintenanceTemplate" | "BioTemplatePremium";

/** Plantillas que el Worker puede inyectar (además de TemplateType en tablas legacy). */
export type KnownRuntimeTemplate =
  | TemplateType
  | "BaseTemplateBlack"
  | "BioTemplatePremiumSimple"
  | "DualCTAVideoLanding"
  | "SorprendemeTemplate"
  | "SorprendemeStripeTemplate";

export interface CountryTemplateConfig {
  countryCode: string;
  countryName: string;
  template: TemplateType;
  language: string; // Now supports regional codes like "es-AR", "en-US", etc.
  currency: string;
  timezone: string[];
  enabled: boolean;
}

/**
 * LATAM Countries Template Configuration
 * 
 * Template Options:
 * - BaseTemplate: Clean international design with gallery and Tally form
 * - BioLinkTemplate: Elegant black bio link design with Mux video CTA (fullscreen reel) for Instagram / Telegram
 */
export const LATAM_COUNTRY_TEMPLATES: CountryTemplateConfig[] = [
  // Argentina — BaseTemplate (coherente con la fuente autoritativa
  // workerCountryTemplateMap.ts / Worker COUNTRY_TEMPLATE_MAP). Este mapa es legacy
  // (solo regionDetection/analytics + debug); el render real lo decide el Worker.
  {
    countryCode: "AR",
    countryName: "Argentina",
    template: "BaseTemplate",
    language: "es-AR",
    currency: "ARS",
    timezone: ["America/Argentina/Buenos_Aires", "America/Argentina/Cordoba"],
    enabled: true,
  },
  
  // Mexico — BioTemplatePremium (USD + Stripe)
  {
    countryCode: "MX", 
    countryName: "México",
    template: "BioTemplatePremium",
    language: "es-MX",
    currency: "MXN",
    timezone: ["America/Mexico_City"],
    enabled: true,
  },
  
  // Colombia
  {
    countryCode: "CO",
    countryName: "Colombia", 
    template: "BioLinkTemplate", // Colombia uses bio link style (same as Spain)
    language: "es-CO",
    currency: "COP",
    timezone: ["America/Bogota"],
    enabled: true,
  },
  
  // Chile
  {
    countryCode: "CL",
    countryName: "Chile",
    template: "BioLinkTemplate", // Chile uses bio link style (same as Spain)
    language: "es-CL", 
    currency: "CLP",
    timezone: ["America/Santiago"],
    enabled: true,
  },
  
  // Peru
  {
    countryCode: "PE",
    countryName: "Perú",
    template: "BioLinkTemplate", // Peru uses bio link style (same as Spain)
    language: "es-PE",
    currency: "PEN", 
    timezone: ["America/Lima"],
    enabled: true,
  },
  
  // Brazil
  {
    countryCode: "BR",
    countryName: "Brasil",
    template: "BioTemplatePremium",
    language: "pt-BR", // Keep Spanish for consistency
    currency: "BRL",
    timezone: ["America/Sao_Paulo", "America/Manaus", "America/Fortaleza"],
    enabled: true,
  },
  
  // Venezuela
  {
    countryCode: "VE",
    countryName: "Venezuela",
    template: "BaseTemplate",
    language: "es-VE",
    currency: "VES",
    timezone: ["America/Caracas"],
    enabled: true,
  },
  
  // Ecuador — BioTemplatePremium (USD + Stripe)
  {
    countryCode: "EC", 
    countryName: "Ecuador",
    template: "BioTemplatePremium",
    language: "es-EC",
    currency: "USD",
    timezone: ["America/Guayaquil"],
    enabled: true,
  },
  
  // Uruguay
  {
    countryCode: "UY",
    countryName: "Uruguay", 
    template: "BioLinkTemplate", // Uruguay uses bio link style (same as Spain)
    language: "es-UY",
    currency: "UYU",
    timezone: ["America/Montevideo"],
    enabled: true,
  },
  
  // Paraguay
  {
    countryCode: "PY",
    countryName: "Paraguay",
    template: "BioLinkTemplate", // Paraguay uses bio link style (same as Spain)
    language: "es-PY",
    currency: "PYG",
    timezone: ["America/Asuncion"],
    enabled: true,
  },
  
  // Bolivia
  {
    countryCode: "BO",
    countryName: "Bolivia",
    template: "BaseTemplate",
    language: "es-BO", 
    currency: "BOB",
    timezone: ["America/La_Paz"],
    enabled: true,
  },
  
  // Costa Rica
  {
    countryCode: "CR",
    countryName: "Costa Rica",
    template: "BaseTemplate",
    language: "es-CR",
    currency: "CRC",
    timezone: ["America/Costa_Rica"],
    enabled: true,
  },
  
  // Panama
  {
    countryCode: "PA",
    countryName: "Panamá", 
    template: "BaseTemplate",
    language: "es-PA",
    currency: "PAB",
    timezone: ["America/Panama"],
    enabled: true,
  },
  
  // Guatemala
  {
    countryCode: "GT",
    countryName: "Guatemala",
    template: "BaseTemplate",
    language: "es-GT",
    currency: "GTQ", 
    timezone: ["America/Guatemala"],
    enabled: true,
  },
  
  // Honduras
  {
    countryCode: "HN",
    countryName: "Honduras",
    template: "BaseTemplate",
    language: "es-HN",
    currency: "HNL",
    timezone: ["America/Tegucigalpa"], 
    enabled: true,
  },
  
  // El Salvador
  {
    countryCode: "SV",
    countryName: "El Salvador",
    template: "BaseTemplate",
    language: "es-SV",
    currency: "USD",
    timezone: ["America/El_Salvador"],
    enabled: true,
  },
  
  // Nicaragua
  {
    countryCode: "NI", 
    countryName: "Nicaragua",
    template: "BaseTemplate",
    language: "es-NI",
    currency: "NIO",
    timezone: ["America/Managua"],
    enabled: true,
  },
  
  // Dominican Republic
  {
    countryCode: "DO",
    countryName: "República Dominicana",
    template: "BaseTemplate",
    language: "es-DO",
    currency: "DOP",
    timezone: ["America/Santo_Domingo"],
    enabled: true,
  },
  
  // Cuba
  {
    countryCode: "CU",
    countryName: "Cuba", 
    template: "BaseTemplate",
    language: "es-CU",
    currency: "CUP",
    timezone: ["America/Havana"],
    enabled: true,
  },
  
  // Puerto Rico
  {
    countryCode: "PR",
    countryName: "Puerto Rico",
    template: "BaseTemplate",
    language: "es-PR",
    currency: "USD",
    timezone: ["America/Puerto_Rico"],
    enabled: true,
  },

  // United States
  {
    countryCode: "US",
    countryName: "United States",
    template: "BioLinkTemplate", // US uses bio link template (English version)
    language: "en-US",
    currency: "USD",
    timezone: ["America/New_York", "America/Los_Angeles", "America/Chicago", "America/Denver"],
    enabled: true,
  },

  // Spain — BioTemplatePremium (USD + Stripe)
  {
    countryCode: "ES",
    countryName: "España",
    template: "BioTemplatePremium",
    language: "es-ES",
    currency: "EUR",
    timezone: ["Europe/Madrid"],
    enabled: true,
  },

  // Germany
  {
    countryCode: "DE",
    countryName: "Germany",
    template: "BioLinkTemplate", // Germany uses bio link template (English version, similar to US)
    language: "en-DE",
    currency: "EUR",
    timezone: ["Europe/Berlin", "Europe/Munich", "Europe/Hamburg"],
    enabled: true,
  },

  // European Countries - All use BioLinkTemplate
  {
    countryCode: "FR",
    countryName: "France",
    template: "BioLinkTemplate",
    language: "en-FR",
    currency: "EUR",
    timezone: ["Europe/Paris"],
    enabled: true,
  },
  {
    countryCode: "IT",
    countryName: "Italy",
    template: "BioLinkTemplate",
    language: "en-IT",
    currency: "EUR",
    timezone: ["Europe/Rome", "Europe/Milan"],
    enabled: true,
  },
  {
    countryCode: "GB",
    countryName: "United Kingdom",
    template: "BioLinkTemplate",
    language: "en-GB",
    currency: "GBP",
    timezone: ["Europe/London"],
    enabled: true,
  },
  {
    countryCode: "NL",
    countryName: "Netherlands",
    template: "BioLinkTemplate",
    language: "en-NL",
    currency: "EUR",
    timezone: ["Europe/Amsterdam"],
    enabled: true,
  },
  {
    countryCode: "BE",
    countryName: "Belgium",
    template: "BioLinkTemplate",
    language: "en-BE",
    currency: "EUR",
    timezone: ["Europe/Brussels"],
    enabled: true,
  },
  {
    countryCode: "CH",
    countryName: "Switzerland",
    template: "BioLinkTemplate",
    language: "en-CH",
    currency: "CHF",
    timezone: ["Europe/Zurich", "Europe/Geneva"],
    enabled: true,
  },
  {
    countryCode: "AT",
    countryName: "Austria",
    template: "BioLinkTemplate",
    language: "en-AT",
    currency: "EUR",
    timezone: ["Europe/Vienna"],
    enabled: true,
  },
  {
    countryCode: "PT",
    countryName: "Portugal",
    template: "BioLinkTemplate",
    language: "en-PT",
    currency: "EUR",
    timezone: ["Europe/Lisbon"],
    enabled: true,
  },
  {
    countryCode: "GR",
    countryName: "Greece",
    template: "BioLinkTemplate",
    language: "en-GR",
    currency: "EUR",
    timezone: ["Europe/Athens"],
    enabled: true,
  },
  {
    countryCode: "IE",
    countryName: "Ireland",
    template: "BioLinkTemplate",
    language: "en-IE",
    currency: "EUR",
    timezone: ["Europe/Dublin"],
    enabled: true,
  },
  {
    countryCode: "DK",
    countryName: "Denmark",
    template: "BioLinkTemplate",
    language: "en-DK",
    currency: "DKK",
    timezone: ["Europe/Copenhagen"],
    enabled: true,
  },
  {
    countryCode: "SE",
    countryName: "Sweden",
    template: "BioLinkTemplate",
    language: "en-SE",
    currency: "SEK",
    timezone: ["Europe/Stockholm"],
    enabled: true,
  },
  {
    countryCode: "NO",
    countryName: "Norway",
    template: "BioLinkTemplate",
    language: "en-NO",
    currency: "NOK",
    timezone: ["Europe/Oslo"],
    enabled: true,
  },
  {
    countryCode: "FI",
    countryName: "Finland",
    template: "BioLinkTemplate",
    language: "en-FI",
    currency: "EUR",
    timezone: ["Europe/Helsinki"],
    enabled: true,
  },
  {
    countryCode: "PL",
    countryName: "Poland",
    template: "BioLinkTemplate",
    language: "en-PL",
    currency: "PLN",
    timezone: ["Europe/Warsaw"],
    enabled: true,
  },
  {
    countryCode: "CZ",
    countryName: "Czech Republic",
    template: "BioLinkTemplate",
    language: "en-CZ",
    currency: "CZK",
    timezone: ["Europe/Prague"],
    enabled: true,
  },
  {
    countryCode: "HU",
    countryName: "Hungary",
    template: "BioLinkTemplate",
    language: "en-HU",
    currency: "HUF",
    timezone: ["Europe/Budapest"],
    enabled: true,
  },
  {
    countryCode: "RO",
    countryName: "Romania",
    template: "BioLinkTemplate",
    language: "en-RO",
    currency: "RON",
    timezone: ["Europe/Bucharest"],
    enabled: true,
  },
  {
    countryCode: "BG",
    countryName: "Bulgaria",
    template: "BioLinkTemplate",
    language: "en-BG",
    currency: "BGN",
    timezone: ["Europe/Sofia"],
    enabled: true,
  },
  {
    countryCode: "HR",
    countryName: "Croatia",
    template: "BioLinkTemplate",
    language: "en-HR",
    currency: "EUR",
    timezone: ["Europe/Zagreb"],
    enabled: true,
  },
  {
    countryCode: "SI",
    countryName: "Slovenia",
    template: "BioLinkTemplate",
    language: "en-SI",
    currency: "EUR",
    timezone: ["Europe/Ljubljana"],
    enabled: true,
  },
  {
    countryCode: "SK",
    countryName: "Slovakia",
    template: "BioLinkTemplate",
    language: "en-SK",
    currency: "EUR",
    timezone: ["Europe/Bratislava"],
    enabled: true,
  },
  {
    countryCode: "LT",
    countryName: "Lithuania",
    template: "BioLinkTemplate",
    language: "en-LT",
    currency: "EUR",
    timezone: ["Europe/Vilnius"],
    enabled: true,
  },
  {
    countryCode: "LV",
    countryName: "Latvia",
    template: "BioLinkTemplate",
    language: "en-LV",
    currency: "EUR",
    timezone: ["Europe/Riga"],
    enabled: true,
  },
  {
    countryCode: "EE",
    countryName: "Estonia",
    template: "BioLinkTemplate",
    language: "en-EE",
    currency: "EUR",
    timezone: ["Europe/Tallinn"],
    enabled: true,
  },
  {
    countryCode: "LU",
    countryName: "Luxembourg",
    template: "BioLinkTemplate",
    language: "en-LU",
    currency: "EUR",
    timezone: ["Europe/Luxembourg"],
    enabled: true,
  },
  {
    countryCode: "MT",
    countryName: "Malta",
    template: "BioLinkTemplate",
    language: "en-MT",
    currency: "EUR",
    timezone: ["Europe/Malta"],
    enabled: true,
  },
  {
    countryCode: "CY",
    countryName: "Cyprus",
    template: "BioLinkTemplate",
    language: "en-CY",
    currency: "EUR",
    timezone: ["Europe/Nicosia"],
    enabled: true,
  },
  {
    countryCode: "IS",
    countryName: "Iceland",
    template: "BioLinkTemplate",
    language: "en-IS",
    currency: "ISK",
    timezone: ["Atlantic/Reykjavik"],
    enabled: true,
  },

  // United Arab Emirates (includes Dubai)
  {
    countryCode: "AE",
    countryName: "United Arab Emirates",
    template: "BioLinkTemplate",
    language: "en-AE",
    currency: "AED",
    timezone: ["Asia/Dubai"],
    enabled: true,
  },
];

/**
 * Get template configuration for a specific country
 */
export const getCountryTemplateConfig = (countryCode: string): CountryTemplateConfig | null => {
  const config = LATAM_COUNTRY_TEMPLATES.find(
    country => country.countryCode === countryCode && country.enabled
  );
  return config || null;
};

function normalizeIso2(code: string | null | undefined): string | null {
  if (code == null || typeof code !== "string") return null;
  const t = code.trim().toUpperCase();
  if (t === "UNKNOWN" || t.length < 2) return null;
  return t.slice(0, 2);
}

/**
 * Plantilla efectiva para un ISO2: si hay `__TEMPLATE_CONFIG__` / cookies del Worker y el país coincide, usa eso;
 * si no, tabla legacy `LATAM_COUNTRY_TEMPLATES`.
 */
export const getTemplateForCountry = (countryCode: string): KnownRuntimeTemplate => {
  const code = normalizeIso2(countryCode);
  if (typeof window !== "undefined" && code) {
    const srv = getServerTemplateConfig();
    const srvCountry = normalizeIso2(srv?.country ?? null);
    if (srv?.templateName && srvCountry === code) {
      return srv.templateName as KnownRuntimeTemplate;
    }
  }
  const config = getCountryTemplateConfig(countryCode);
  return (config?.template ?? "BaseTemplate") as KnownRuntimeTemplate;
};

/**
 * Check if a country should use BioLinkTemplate
 */
export const shouldCountryUseBioLink = (countryCode: string): boolean => {
  return getTemplateForCountry(countryCode) === "BioLinkTemplate";
};

/**
 * Get all countries using BioLinkTemplate
 */
export const getBioLinkCountries = (): CountryTemplateConfig[] => {
  return LATAM_COUNTRY_TEMPLATES.filter(
    country => country.template === "BioLinkTemplate" && country.enabled
  );
};

/**
 * Get all countries using BaseTemplate
 */
export const getBaseTemplateCountries = (): CountryTemplateConfig[] => {
  return LATAM_COUNTRY_TEMPLATES.filter(
    country => country.template === "BaseTemplate" && country.enabled
  );
};

/**
 * Get all countries using BioTemplatePremium (checkout global: ARS o USD).
 * Para activar un país: cambiar template: "BioTemplatePremium" en LATAM_COUNTRY_TEMPLATES.
 */
export const getPremiumTemplateCountries = (): CountryTemplateConfig[] => {
  return LATAM_COUNTRY_TEMPLATES.filter(
    country => country.template === "BioTemplatePremium" && country.enabled
  );
};

/**
 * Check if a country uses BioTemplatePremium.
 */
export const isPremiumTemplateCountry = (countryCode: string): boolean => {
  const t = getTemplateForCountry(countryCode);
  if (t === "BioTemplatePremium" || t === "BioTemplatePremiumSimple") return true;
  return getPremiumTemplateCountries().some((c) => c.countryCode === countryCode);
};

/**
 * Helper function to detect country from timezone
 */
export const detectCountryFromTimezone = (timezone: string): string | null => {
  // Direct timezone matches first (most accurate)
  for (const country of LATAM_COUNTRY_TEMPLATES) {
    if (country.enabled && country.timezone.includes(timezone)) {
      return country.countryCode;
    }
  }
  
  // Fallback: partial matches for complex timezone names
  for (const country of LATAM_COUNTRY_TEMPLATES) {
    if (country.enabled && country.timezone.some(tz => {
      const cityName = tz.split('/')[1];
      return timezone.includes(cityName);
    })) {
      return country.countryCode;
    }
  }
  
  return null;
};

/**
 * Helper function to detect country from browser language
 */
export const detectCountryFromLanguage = (language: string): string | null => {
  const countryCode = language.split('-')[1]?.toUpperCase();
  if (countryCode) {
    const config = getCountryTemplateConfig(countryCode);
    return config ? countryCode : null;
  }
  return null;
};

// Export default configuration for easy access
export default {
  LATAM_COUNTRY_TEMPLATES,
  getCountryTemplateConfig,
  getTemplateForCountry,
  shouldCountryUseBioLink,
  getBioLinkCountries,
  getBaseTemplateCountries,
  detectCountryFromTimezone,
  detectCountryFromLanguage,
};